import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePacking } from '../../hooks/usePacking';
import { useFamilyContext } from '../../contexts/FamilyContext';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  MapPin,
  Calendar,
  Cloud,
  Users,
  Archive,
  Download,
  Share2,
  Filter,
  Search,
  Package,
  Sparkles,
  MoreVertical,
  CheckSquare,
  Square,
  User,
  Save,
  Briefcase,
  Backpack,
  ShoppingBag,
  Luggage,
  Eye,
  EyeOff,
  Bot,
  Lightbulb,
  Settings,
  SortAsc,
  Shirt,
  Smartphone,
  FileText,
  Pill,
  Gamepad2,
  Baby,
  PawPrint,
  Droplets,
  Printer,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';
import { packingAIService } from '../../lib/packing-ai-service';
import { weatherService } from '../../lib/weather-service';

// Trip type icons mapping - prefix with underscore to indicate intentionally unused for now
const _getTripTypeIcon = (type) => {
  const icons = {
    vacation: Luggage,
    business: Briefcase,
    camping: Backpack,
    beach: Luggage,
    winter_sports: Luggage,
    international: Luggage,
    weekend: ShoppingBag,
    other: Luggage
  };
  return icons[type] || Luggage;
};

export default function PackingListDetailEnhanced() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lists, addItem, updateItem, deleteItem, toggleItemPacked, updateList, deleteList, addToFavorites, favorites, getSuggestedItems, saveAsTemplate } = usePacking();
  const { family } = useFamilyContext();
  
  const [list, setList] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter] = useState('all'); // Removed unused setter
  const [assigneeFilter] = useState('all'); // Removed unused setter
  const [showSuggestions] = useState(false); // Removed unused setter
  const [_suggestedItems, setSuggestedItems] = useState([]); // Prefixed unused variable
  const [showMenu, setShowMenu] = useState(false);
  const [groupBy, setGroupBy] = useState('luggage_type'); // category, luggage_type, assignee
  const [showCheckedItems, setShowCheckedItems] = useState(true);
  const [isEditingList, setIsEditingList] = useState(false);
  const [showAIAssist, setShowAIAssist] = useState(false);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [suggestionType, setSuggestionType] = useState('');
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [itemToCopy, setItemToCopy] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  const [itemsInTransition, setItemsInTransition] = useState(new Set());
  
  const [newItem, setNewItem] = useState({
    name: '',
    quantity: 1,
    category: 'other',
    notes: '',
    luggage_type: 'Checked Luggage',
    assigned_to: null
  });

  const [editListData, setEditListData] = useState({
    name: '',
    destination: '',
    start_date: '',
    end_date: '',
    trip_type: '',
    weather_expected: '',
    list_type: 'individual'
  });
  const [editTripLegs, setEditTripLegs] = useState([]);

  // Find the list
  useEffect(() => {
    const currentList = lists.find(l => l.id === id);
    if (currentList) {
      // Debug weather data
      console.log('🌤️ Weather Debug - Current List:', {
        id: currentList.id,
        name: currentList.name,
        weather_expected: currentList.weather_expected,
        weather_data: currentList.weather_data,
        trip_legs: currentList.trip_legs,
        destination: currentList.destination
      });
      
      if (currentList.trip_legs) {
        currentList.trip_legs.forEach((leg, index) => {
          console.log(`🌤️ Weather Debug - Trip Leg ${index + 1}:`, {
            location: leg.location,
            weather_data: leg.weather_data,
            zip_code: leg.zip_code,
            start_date: leg.start_date,
            end_date: leg.end_date
          });
        });
      }
      
      setList(currentList);
      
      // Fetch missing weather data for trip legs
      fetchMissingWeatherData(currentList);
      
      setEditListData({
        name: currentList.name || '',
        destination: currentList.destination || '',
        start_date: currentList.start_date || '',
        end_date: currentList.end_date || '',
        trip_type: currentList.trip_type || 'vacation',
        weather_expected: currentList.weather_expected || 'mild',
        list_type: currentList.list_type || 'individual'
      });

      // Initialize trip legs
      setEditTripLegs(currentList.trip_legs && currentList.trip_legs.length > 0 
        ? currentList.trip_legs 
        : [{
            id: 1,
            location: currentList.destination || '',
            zip_code: '',
            start_date: currentList.start_date || '',
            end_date: currentList.end_date || '',
            weather_data: null
          }]
      );
    }
  }, [lists, id]);

  // Load suggested items
  useEffect(() => {
    const loadSuggestions = async () => {
      if (list && showSuggestions) {
        const suggestions = await getSuggestedItems(list.trip_type);
        setSuggestedItems(suggestions);
      }
    };
    loadSuggestions();
  }, [list, showSuggestions, getSuggestedItems]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;

    try {
      await addItem(list.id, newItem);
      
      // Add to favorites if it's a new item
      if (!favorites.some(f => f.name.toLowerCase() === newItem.name.toLowerCase())) {
        await addToFavorites(newItem.name, newItem.category, list.trip_type);
      }
      
      setNewItem({
        name: '',
        quantity: 1,
        category: 'other',
        notes: '',
        luggage_type: 'Checked Luggage',
        assigned_to: null
      });
      setShowAddItem(false);
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      await updateItem(itemId, updates);
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteItem(itemId);
      } catch (error) {
        console.error('Failed to delete item:', error);
      }
    }
  };

  // AI Assistant Functions
  const showAISuggestions = async (type) => {
    try {
      let suggestions = [];
      
      switch (type) {
        case 'essentials':
          suggestions = packingAIService.getTripTypeSuggestions(list.trip_type, getDurationDays());
          break;
        case 'weather':
          suggestions = packingAIService.getWeatherSuggestions(list.weather_expected, list.destination);
          break;
        case 'activities':
          suggestions = packingAIService.getActivitySuggestions(['dining', 'sightseeing']);
          break;
        case 'forgotten':
          suggestions = packingAIService.getCommonlyForgottenItems();
          break;
        default:
          return;
      }

      // Filter out items that already exist and prepare for preview
      const filteredSuggestions = suggestions.slice(0, 6).map(suggestion => {
        const itemExists = list.items?.some(item => 
          item.name.toLowerCase().includes(suggestion.name.toLowerCase()) ||
          suggestion.name.toLowerCase().includes(item.name.toLowerCase())
        );
        
        return {
          ...suggestion,
          quantity: packingAIService.suggestQuantity(suggestion.name, getDurationDays()),
          luggage_type: 'Checked Luggage',
          notes: `AI suggested for ${type}`,
          selected: !itemExists, // Auto-select new items, deselect existing ones
          exists: itemExists
        };
      });

      setPendingSuggestions(filteredSuggestions);
      setSuggestionType(type);
      setShowSuggestionsModal(true);
    } catch (error) {
      console.error('Failed to get AI suggestions:', error);
    }
  };

  const addSelectedSuggestions = async () => {
    try {
      const selectedSuggestions = pendingSuggestions.filter(s => s.selected && !s.exists);
      
      for (const suggestion of selectedSuggestions) {
        await addItem(list.id, {
          name: suggestion.name,
          category: suggestion.category,
          quantity: suggestion.quantity,
          luggage_type: suggestion.luggage_type,
          notes: suggestion.notes
        });
      }
      
      setShowSuggestionsModal(false);
      setPendingSuggestions([]);
    } catch (error) {
      console.error('Failed to add selected suggestions:', error);
    }
  };

  const toggleSuggestionSelection = (index) => {
    setPendingSuggestions(prev => 
      prev.map((suggestion, i) => 
        i === index ? { ...suggestion, selected: !suggestion.selected } : suggestion
      )
    );
  };

  const toggleCategoryCollapse = (category) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Trip legs management for edit form
  const addEditTripLeg = () => {
    const newId = Math.max(...editTripLegs.map(leg => leg.id)) + 1;
    setEditTripLegs([...editTripLegs, {
      id: newId,
      location: '',
      zip_code: '',
      start_date: '',
      end_date: '',
      weather_data: null
    }]);
  };

  const removeEditTripLeg = (legId) => {
    if (editTripLegs.length > 1) {
      setEditTripLegs(editTripLegs.filter(leg => leg.id !== legId));
    }
  };

  const updateEditTripLeg = (legId, field, value) => {
    setEditTripLegs(editTripLegs.map(leg => 
      leg.id === legId ? { ...leg, [field]: value } : leg
    ));
  };

  const getDurationDays = () => {
    if (!list.start_date || !list.end_date) return 7; // Default to 7 days
    const start = new Date(list.start_date);
    const end = new Date(list.end_date);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
  };

  const handleTogglePacked = async (item) => {
    try {
      // Add item to transition state immediately for visual feedback
      setItemsInTransition(prev => new Set([...prev, item.id]));
      
      // Toggle the packed status
      await toggleItemPacked(item.id);
      
      // Remove from transition state after a brief delay to show the crossed-out effect
      setTimeout(() => {
        setItemsInTransition(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      }, 1200); // 1.2 second delay to show the effect
      
    } catch (error) {
      console.error('Failed to toggle packed status:', error);
      // Remove from transition state on error
      setItemsInTransition(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  const handleUpdateList = async () => {
    try {
      const updateData = {
        ...editListData,
        trip_legs: editTripLegs.filter(leg => leg.location) // Only include legs with locations
      };
      
      // If switching from group to individual, reset groupBy if it was set to assignee
      if (editListData.list_type === 'individual' && groupBy === 'assignee') {
        setGroupBy('luggage_type');
      }
      
      await updateList(list.id, updateData);
      setIsEditingList(false);
    } catch (error) {
      console.error('Failed to update list:', error);
    }
  };

  const handleArchiveList = async () => {
    if (window.confirm('Archive this packing list? You can still view it later.')) {
      try {
        await updateList(list.id, { status: 'archived' });
        navigate('/packing');
      } catch (error) {
        console.error('Failed to archive list:', error);
      }
    }
  };

  const handleDeleteList = async () => {
    if (window.confirm('Are you sure you want to delete this packing list? This cannot be undone.')) {
      try {
        await deleteList(list.id);
        navigate('/packing');
      } catch (error) {
        console.error('Failed to delete list:', error);
      }
    }
  };

  const handleSaveAsTemplate = async () => {
    const templateName = prompt('Template name:', `${list.name} Template`);
    if (templateName) {
      try {
        await saveAsTemplate(list.id, templateName);
        alert('Template saved successfully!');
      } catch (error) {
        console.error('Failed to save template:', error);
      }
    }
  };

  const handlePrintList = () => {
    // Create a compact two-column print-friendly version of the packing list
    const printWindow = window.open('', '_blank');
    const packedItems = list.items?.filter(item => item.packed) || [];
    const unpackedItems = list.items?.filter(item => !item.packed) || [];
    const totalItems = list.items?.length || 0;
    const packedCount = packedItems.length;
    const progress = totalItems > 0 ? Math.round((packedCount / totalItems) * 100) : 0;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${list.name} - Packing List</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              margin: 0; 
              padding: 0.5in; 
              font-size: 11px;
              line-height: 1.3;
            }
            h1 { 
              color: #334155; 
              border-bottom: 1px solid #ef4444; 
              padding-bottom: 4px; 
              margin: 0 0 12px 0;
              font-size: 16px;
            }
            h2 { 
              color: #475569; 
              margin: 16px 0 8px 0; 
              font-size: 13px;
              font-weight: 600;
            }
            .trip-info { 
              background: #f8fafc; 
              padding: 8px; 
              border-radius: 4px; 
              margin: 8px 0; 
              border-left: 3px solid #ef4444;
              font-size: 10px;
            }
            .trip-info p { margin: 2px 0; }
            .progress { 
              background: #e2e8f0; 
              height: 4px; 
              border-radius: 2px; 
              margin: 4px 0; 
            }
            .progress-bar { 
              background: #ef4444; 
              height: 100%; 
              border-radius: 2px; 
              width: ${progress}%;
            }
            .two-column { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 16px; 
              margin: 12px 0;
            }
            .column { 
              break-inside: avoid;
            }
            .item-list { 
              list-style: none; 
              padding: 0; 
              margin: 0;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 4px 12px;
            }
            .item { 
              padding: 3px 0; 
              display: flex;
              align-items: flex-start;
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .packed { color: #16a34a; text-decoration: line-through; opacity: 0.8; }
            .unpacked { color: #334155; }
            .checkbox { 
              width: 12px; 
              height: 12px; 
              border: 1px solid #d1d5db; 
              border-radius: 2px; 
              display: inline-block; 
              margin-right: 6px;
              margin-top: 1px;
              flex-shrink: 0;
            }
            .checked { 
              background: #16a34a; 
              border-color: #16a34a; 
              position: relative;
            }
            .checked::after {
              content: '✓';
              color: white;
              font-size: 8px;
              position: absolute;
              top: -1px;
              left: 1px;
            }
            .item-content {
              flex: 1;
              min-width: 0;
            }
            .item-name {
              font-weight: 500;
              font-size: 11px;
            }
            .item-details {
              font-size: 9px;
              color: #64748b;
              margin-top: 1px;
            }
            .section { 
              margin: 16px 0 8px 0; 
              break-inside: avoid;
            }
            .footer {
              margin-top: 20px; 
              padding-top: 8px; 
              border-top: 1px solid #e2e8f0; 
              color: #64748b; 
              font-size: 9px; 
              text-align: center;
            }
            @media print { 
              body { 
                margin: 0; 
                padding: 0.4in; 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .no-print { display: none; }
              .section { page-break-inside: avoid; }
              .item { page-break-inside: avoid; }
              h2 { page-break-after: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>${list.name}</h1>
          
          <div class="trip-info">
            ${list.destination ? `<p><strong>Destination:</strong> ${list.destination}</p>` : ''}
            ${list.start_date ? `<p><strong>Dates:</strong> ${format(new Date(list.start_date + 'T00:00:00'), 'MMM d, yyyy')}${list.end_date ? ` - ${format(new Date(list.end_date + 'T00:00:00'), 'MMM d, yyyy')}` : ''}</p>` : ''}
            <p><strong>Progress:</strong> ${packedCount} of ${totalItems} items packed (${progress}%)</p>
            <div class="progress">
              <div class="progress-bar"></div>
            </div>
          </div>

          ${unpackedItems.length > 0 ? `
          <div class="section">
            <h2>Items to Pack (${unpackedItems.length})</h2>
            <ul class="item-list">
              ${unpackedItems.map(item => `
                <li class="item unpacked">
                  <span class="checkbox"></span>
                  <div class="item-content">
                    <div class="item-name">${item.name}${item.quantity > 1 ? ` (${item.quantity})` : ''}</div>
                    <div class="item-details">${item.category || 'other'}${item.luggage_type && item.luggage_type !== 'Checked Luggage' ? ` • ${item.luggage_type}` : ''}</div>
                  </div>
                </li>
              `).join('')}
            </ul>
          </div>
          ` : ''}

          ${packedItems.length > 0 ? `
          <div class="section">
            <h2>Packed Items (${packedItems.length})</h2>
            <ul class="item-list">
              ${packedItems.map(item => `
                <li class="item packed">
                  <span class="checkbox checked"></span>
                  <div class="item-content">
                    <div class="item-name">${item.name}${item.quantity > 1 ? ` (${item.quantity})` : ''}</div>
                    <div class="item-details">${item.category || 'other'}${item.luggage_type && item.luggage_type !== 'Checked Luggage' ? ` • ${item.luggage_type}` : ''}</div>
                  </div>
                </li>
              `).join('')}
            </ul>
          </div>
          ` : ''}

          <div class="footer">
            Generated from PaperSedge on ${format(new Date(), 'MMM d, yyyy \'at\' h:mm a')}
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleCopyItem = (item) => {
    setItemToCopy(item);
    setShowCopyModal(true);
  };

  const handleCopyToLists = async (selectedListIds) => {
    if (!itemToCopy || selectedListIds.length === 0) return;

    try {
      // Copy the item to each selected list
      const copyPromises = selectedListIds.map(async (listId) => {
        const itemData = {
          name: itemToCopy.name,
          quantity: itemToCopy.quantity || 1,
          category: itemToCopy.category || 'other',
          notes: itemToCopy.notes || '',
          luggage_type: itemToCopy.luggage_type || 'Checked Luggage',
        };
        
        return addItem(listId, itemData);
      });

      await Promise.all(copyPromises);
      
      // Show success message
      const listNames = lists
        .filter(l => selectedListIds.includes(l.id))
        .map(l => l.name)
        .join(', ');
      
      alert(`Successfully copied "${itemToCopy.name}" to ${selectedListIds.length} list(s): ${listNames}`);
      
      setShowCopyModal(false);
      setItemToCopy(null);
    } catch (error) {
      console.error('Error copying item:', error);
      alert('Failed to copy item. Please try again.');
    }
  };

  // Filter items
  const filteredItems = list?.items?.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    const matchesAssignee = assigneeFilter === 'all' || 
      (assigneeFilter === 'unassigned' && !item.assigned_to) ||
      item.assigned_to === assigneeFilter;
    const matchesChecked = showCheckedItems || !item.packed;
    return matchesSearch && matchesCategory && matchesAssignee && matchesChecked;
  }) || [];

  // Sort items with unpacked first, packed at bottom (but keep transitioning items in original position)
  const sortItemsByPackedStatus = (items) => {
    return [...items].sort((a, b) => {
      // Don't move items that are currently transitioning
      if (itemsInTransition.has(a.id) || itemsInTransition.has(b.id)) {
        return 0; // Keep original order for transitioning items
      }
      
      // Sort by packed status: unpacked (false) first, packed (true) last
      if (a.packed === b.packed) return 0;
      return a.packed ? 1 : -1;
    });
  };

  // Group items based on selected grouping
  const groupItems = (items) => {
    if (groupBy === 'luggage_type') {
      // Hierarchical grouping: luggage type > category
      const luggageGroups = {};
      
      items.forEach(item => {
        const type = item.luggage_type || 'Checked Luggage';
        const category = item.category || 'other';
        
        if (!luggageGroups[type]) {
          luggageGroups[type] = {};
        }
        if (!luggageGroups[type][category]) {
          luggageGroups[type][category] = [];
        }
        
        luggageGroups[type][category].push(item);
      });
      
      // Sort items within each category
      Object.keys(luggageGroups).forEach(type => {
        Object.keys(luggageGroups[type]).forEach(category => {
          luggageGroups[type][category] = sortItemsByPackedStatus(luggageGroups[type][category]);
        });
      });
      
      // Sort luggage types to put "To Wear" first and "Checked Luggage" last
      const sortedLuggageGroups = {};
      const orderedTypes = ['To Wear', 'Carry-On', 'Personal Item', 'Checked Luggage'];
      
      orderedTypes.forEach(type => {
        if (luggageGroups[type]) {
          sortedLuggageGroups[type] = luggageGroups[type];
        }
      });
      
      // Add any other types not in the ordered list
      Object.keys(luggageGroups).forEach(type => {
        if (!orderedTypes.includes(type)) {
          sortedLuggageGroups[type] = luggageGroups[type];
        }
      });
      
      return sortedLuggageGroups;
    } else if (groupBy === 'assignee') {
      const groups = items.reduce((groups, item) => {
        const assignee = item.assigned_to || 'unassigned';
        if (!groups[assignee]) groups[assignee] = [];
        groups[assignee].push(item);
        return groups;
      }, {});
      
      // Sort items within each assignee group
      Object.keys(groups).forEach(assignee => {
        groups[assignee] = sortItemsByPackedStatus(groups[assignee]);
      });
      
      return groups;
    } else {
      // Default: group by category
      const groups = items.reduce((groups, item) => {
        const category = item.category || 'other';
        if (!groups[category]) groups[category] = [];
        groups[category].push(item);
        return groups;
      }, {});
      
      // Sort items within each category
      Object.keys(groups).forEach(category => {
        groups[category] = sortItemsByPackedStatus(groups[category]);
      });
      
      return groups;
    }
  };

  const groupedItems = groupItems(filteredItems);

  // Calculate stats
  const totalItems = list?.items?.length || 0;
  const packedItems = list?.items?.filter(i => i.packed).length || 0;
  const progress = totalItems > 0 ? Math.round((packedItems / totalItems) * 100) : 0;

  // Get weather icon
  const getWeatherIcon = (weather) => {
    switch(weather) {
      case 'hot': return '☀️';
      case 'warm': return '🌤️';
      case 'mild': return '☁️';
      case 'cold': return '❄️';
      case 'rainy': return '🌧️';
      case 'mixed': return '🌦️';
      default: return '☁️';
    }
  };

  // Get weather warnings for special packing considerations
  const getWeatherWarnings = (weatherData) => {
    if (!weatherData || !weatherData.forecast || weatherData.forecast.length === 0) {
      return [];
    }

    const warnings = [];
    const temps = weatherData.forecast.map(d => [d.tempMin, d.tempMax]).flat();
    const minTemp = Math.min(...temps);
    const maxTemp = Math.max(...temps);
    const tempRange = maxTemp - minTemp;
    
    // Temperature-based warnings
    if (minTemp <= 32) warnings.push('❄️ Freezing temps - pack warm layers');
    else if (minTemp <= 45) warnings.push('🧥 Cold weather - bring jacket');
    
    if (maxTemp >= 90) warnings.push('🌡️ Very hot - pack sun protection');
    else if (maxTemp >= 80) warnings.push('☀️ Hot weather - light clothing');
    
    // Range-based warnings
    if (tempRange > 40) warnings.push('🌡️ Big temp swings - layer up');
    
    // Precipitation warnings
    const rainyDays = weatherData.forecast.filter(d => 
      d.description && (d.description.includes('rain') || d.description.includes('storm'))
    ).length;
    
    if (rainyDays > 0) {
      warnings.push(`🌧️ Rain expected ${rainyDays} day(s) - pack waterproof gear`);
    }
    
    return warnings.slice(0, 2); // Limit to 2 most important warnings
  };

  // Fetch weather data for trip legs that don't have it
  const fetchMissingWeatherData = async (listToUpdate) => {
    if (!listToUpdate?.trip_legs || listToUpdate.trip_legs.length === 0) {
      return;
    }

    console.log('🌤️ Checking trip legs for missing weather data...');
    
    let hasUpdates = false;
    const updatedTripLegs = await Promise.all(
      listToUpdate.trip_legs.map(async (leg, index) => {
        // Check if leg has required data but no weather_data OR has mock weather_data
        const hasMockData = leg.weather_data?.message?.includes('mock weather data');
        const needsWeatherData = !leg.weather_data || hasMockData;
        
        if (leg.zip_code && leg.start_date && leg.end_date && needsWeatherData) {
          console.log(`🌤️ Fetching weather for trip leg ${index + 1}: ${leg.location}`);
          
          try {
            const weather = await weatherService.getWeatherForTrip(
              leg.zip_code,
              leg.start_date,
              leg.end_date
            );
            
            if (weather) {
              console.log(`🌤️ Got weather data for ${leg.location}:`, weather);
              hasUpdates = true;
              return { ...leg, weather_data: weather };
            }
          } catch (error) {
            console.error(`🌤️ Failed to fetch weather for ${leg.location}:`, error);
          }
        }
        
        return leg;
      })
    );

    // Update the list in the database if we got new weather data
    if (hasUpdates) {
      console.log('🌤️ Updating list with new weather data...');
      try {
        await updateList(listToUpdate.id, { trip_legs: updatedTripLegs });
        console.log('🌤️ Successfully updated list with weather data');
      } catch (error) {
        console.error('🌤️ Failed to update list with weather data:', error);
      }
    }
  };

  if (!list) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500 mx-auto mb-4"></div>
          <p className="text-stone-600 font-medium">Loading packing list...</p>
        </div>
      </div>
    );
  }

  const getCategoryIcon = (category) => {
    const iconConfig = {
      clothing: { icon: Shirt, color: 'text-azure-600', bg: 'bg-azure-50', headerBg: 'bg-azure-100' },
      toiletries: { icon: Droplets, color: 'text-cyan-600', bg: 'bg-emerald-50', headerBg: 'bg-emerald-100' },
      electronics: { icon: Smartphone, color: 'text-violet-600', bg: 'bg-azure-50', headerBg: 'bg-azure-100' },
      documents: { icon: FileText, color: 'text-orange-600', bg: 'bg-orange-50', headerBg: 'bg-orange-100' },
      medications: { icon: Pill, color: 'text-red-600', bg: 'bg-yellow-50', headerBg: 'bg-yellow-100' },
      entertainment: { icon: Gamepad2, color: 'text-emerald-600', bg: 'bg-emerald-50', headerBg: 'bg-emerald-100' },
      accessories: { icon: ShoppingBag, color: 'text-pink-600', bg: 'bg-pink-50', headerBg: 'bg-pink-100' },
      shoes: { icon: Package, color: 'text-amber-600', bg: 'bg-orange-50', headerBg: 'bg-orange-100' },
      outdoor_gear: { icon: Backpack, color: 'text-emerald-600', bg: 'bg-emerald-50', headerBg: 'bg-emerald-100' },
      baby_items: { icon: Baby, color: 'text-rose-600', bg: 'bg-rose-50', headerBg: 'bg-rose-100' },
      pet_supplies: { icon: PawPrint, color: 'text-indigo-600', bg: 'bg-indigo-50', headerBg: 'bg-indigo-100' },
      other: { icon: Package, color: 'text-stone-600', bg: 'bg-stone-50', headerBg: 'bg-stone-100' }
    };
    const config = iconConfig[category] || iconConfig.other;
    const IconComponent = config.icon;
    return {
      icon: <IconComponent className={`w-3 h-3 ${config.color}`} />,
      color: config.color,
      bg: config.bg,
      headerBg: config.headerBg
    };
  };

  const luggageTypeLabels = {
    'To Wear': 'To Wear',
    'Carry-On': 'Carry-On',
    'Checked Luggage': 'Checked Luggage', 
    'Personal Item': 'Personal Item',
    'Any Luggage': 'Any Luggage'
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Ultra-Compact Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Header Top Row - Ultra Compact */}
          <div className="flex items-start justify-between mb-2">
            <button 
              onClick={() => navigate('/packing')}
              className="p-2 text-stone-600 hover:bg-stone-100 rounded transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex-1 mx-3">
              {isEditingList ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editListData.name}
                    onChange={(e) => setEditListData({...editListData, name: e.target.value})}
                    className="text-lg font-semibold text-slate-900 bg-transparent border-b border-coral-500 focus:outline-none"
                    autoFocus
                  />
                  <button
                    onClick={handleUpdateList}
                    className="p-1 text-coral-600 hover:bg-coral-50 rounded"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsEditingList(false)}
                    className="p-1 text-stone-600 hover:bg-stone-100 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  {/* Title and Date on Same Line */}
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-lg font-semibold text-slate-900">{list.name}</h1>
                    <button
                      onClick={() => setIsEditingList(true)}
                      className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    
                  </div>
                  
                  {/* Date and Location Row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {list.start_date && list.end_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-stone-500" />
                        <span className="text-xs text-stone-600 font-medium">
                          {format(new Date(list.start_date + 'T00:00:00'), 'MMM d')}-{format(new Date(list.end_date + 'T00:00:00'), 'd, yyyy')}
                        </span>
                      </div>
                    )}
                    
                    {/* Location with weather - Mobile and Desktop */}
                    {(list.trip_legs?.length > 0 || list.destination) && (
                      <button
                        onClick={() => setShowWeatherModal(true)}
                        className="md:pointer-events-none flex items-center gap-1 text-stone-600 hover:text-stone-800 transition-colors p-1 -m-1 rounded md:p-0 md:m-0"
                      >
                        <MapPin className="w-3 h-3 text-stone-500" />
                        <span className="text-xs text-stone-600 font-medium">
                          {list.trip_legs?.length > 0 ? list.trip_legs[0].location : list.destination}
                        </span>
                        {/* Weather indicator */}
                        {(list.trip_legs?.[0]?.weather_data || list.weather_data || list.weather_expected) && (
                          <span className="text-sm md:hidden">
                            {list.trip_legs?.[0]?.weather_data ? 
                              getWeatherIcon(list.trip_legs[0].weather_data.weatherCategory || 'mild') :
                              list.weather_data ? 
                                getWeatherIcon(list.weather_data.weatherCategory || 'mild') :
                                getWeatherIcon(list.weather_expected || 'mild')
                            }
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Weather Warnings - Desktop Only */}
                  <div className="text-xs hidden md:block">
                    {((list.trip_legs && list.trip_legs.some(leg => getWeatherWarnings(leg.weather_data).length > 0)) || 
                      (list.weather_data && getWeatherWarnings(list.weather_data).length > 0)) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {list.trip_legs ? (
                          list.trip_legs.flatMap(leg => getWeatherWarnings(leg.weather_data)).slice(0, 2).map((warning, idx) => (
                            <div key={idx} className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                              {warning}
                            </div>
                          ))
                        ) : (
                          getWeatherWarnings(list.weather_data).slice(0, 2).map((warning, idx) => (
                            <div key={idx} className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                              {warning}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 text-stone-600 hover:bg-stone-100 rounded transition-colors"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
          </div>
          
          {/* Menu Dropdown */}
          {showMenu && (
            <div className="absolute right-4 mt-1 w-48 bg-white rounded-lg border border-stone-200 shadow-lg overflow-hidden z-10">
              <button
                onClick={() => {
                  setIsEditingList(true);
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Edit List Details
              </button>
              <button
                onClick={() => {
                  handleSaveAsTemplate();
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save as Template
              </button>
              <button
                onClick={() => {
                  handlePrintList();
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print List
              </button>
              <button
                onClick={() => {
                  handleArchiveList();
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-stone-50 text-sm flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                Archive List
              </button>
              <button
                onClick={() => {
                  handleDeleteList();
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-red-50 text-sm flex items-center gap-2 text-red-600"
              >
                <Trash2 className="w-4 h-4" />
                Delete List
              </button>
            </div>
          )}
        </div>

        {/* Edit List Form */}
        {isEditingList && (
          <div className="bg-stone-50 border-t border-stone-200">
            <div className="max-w-7xl mx-auto px-6 pb-4">
            
            {/* Basic List Details */}
            <div className="mt-4">
              <h3 className="text-sm font-medium text-slate-900 mb-3">List Settings</h3>
              <div className="mb-4 p-4 bg-white rounded-lg border border-stone-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">List Type</label>
                    <select
                      value={editListData.list_type}
                      onChange={(e) => setEditListData({...editListData, list_type: e.target.value})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    >
                      <option value="individual">Individual</option>
                      <option value="group">Group</option>
                    </select>
                    <p className="text-xs text-stone-500 mt-1">
                      {editListData.list_type === 'individual' ? 'Personal packing list' : 'Shared list with assignments'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Trip Type</label>
                    <select
                      value={editListData.trip_type}
                      onChange={(e) => setEditListData({...editListData, trip_type: e.target.value})}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    >
                      <option value="vacation">Vacation</option>
                      <option value="business">Business</option>
                      <option value="camping">Camping</option>
                      <option value="beach">Beach</option>
                      <option value="winter_sports">Winter Sports</option>
                      <option value="international">International</option>
                      <option value="weekend">Weekend</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              <h3 className="text-sm font-medium text-slate-900 mb-3">Trip Locations</h3>
              
              {editTripLegs.map((leg, index) => (
                <div key={leg.id} className="mb-4 p-4 bg-white rounded-lg border border-stone-200">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-slate-900">
                      {index === 0 ? 'Primary Location' : `Location ${index + 1}`}
                    </h5>
                    {editTripLegs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEditTripLeg(leg.id)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Location name"
                      value={leg.location}
                      onChange={(e) => updateEditTripLeg(leg.id, 'location', e.target.value)}
                      className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    />
                    <input
                      type="text"
                      placeholder="Zip code (for weather)"
                      value={leg.zip_code}
                      onChange={(e) => updateEditTripLeg(leg.id, 'zip_code', e.target.value)}
                      className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    />
                    <input
                      type="date"
                      value={leg.start_date}
                      onChange={(e) => updateEditTripLeg(leg.id, 'start_date', e.target.value)}
                      className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    />
                    <input
                      type="date"
                      value={leg.end_date}
                      onChange={(e) => updateEditTripLeg(leg.id, 'end_date', e.target.value)}
                      className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    />
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={addEditTripLeg}
                className="flex items-center gap-2 px-3 py-2 text-sm text-coral-600 hover:text-coral-700 hover:bg-coral-50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Another Location
              </button>
            </div>
            </div>
          </div>
        )}

        {/* Ultra-Compact Progress and Controls */}
        {!isEditingList && (
          <div className="max-w-7xl mx-auto pl-12 pr-4 pb-2">
            {/* Mobile: Simple Stats + Actions */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-2">
                {/* Stats on left */}
                <div className="flex gap-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-slate-900 leading-none">{totalItems}</div>
                    <div className="text-xs text-stone-600 uppercase tracking-wide leading-none">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-coral-600 leading-none">{packedItems}</div>
                    <div className="text-xs text-stone-600 uppercase tracking-wide leading-none">Packed</div>
                  </div>
                </div>
                
                {/* Action Buttons on right */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowAddItem(!showAddItem)}
                    className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                      showAddItem 
                        ? 'bg-stone-600 text-white' 
                        : 'bg-coral-500 text-white hover:bg-coral-600'
                    }`}
                    aria-label={showAddItem ? "Close add item form" : "Add new item"}
                  >
                    <Plus className={`w-4 h-4 transition-transform ${showAddItem ? 'rotate-45' : ''}`} />
                  </button>

                  <button
                    onClick={() => setShowAIAssist(!showAIAssist)}
                    className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                      showAIAssist 
                        ? 'bg-violet-700 text-white' 
                        : 'bg-violet-600 text-white hover:bg-violet-700'
                    }`}
                    aria-label={showAIAssist ? "Hide AI assistant" : "Show AI assistant"}
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => setShowCheckedItems(!showCheckedItems)}
                    className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                      showCheckedItems 
                        ? 'bg-emerald-600 text-white hover:bg-slate-900' 
                        : 'bg-stone-400 text-white hover:bg-stone-500'
                    }`}
                    aria-label={showCheckedItems ? "Hide packed items" : "Show packed items"}
                  >
                    {showCheckedItems ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop: Full Stats + Progress + Search + Filter */}
            <div className="hidden md:flex items-center gap-3 mb-2">
              {/* Compact Stats */}
              <div className="flex gap-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-900 leading-none">{totalItems}</div>
                  <div className="text-xs text-stone-600 uppercase tracking-wide leading-none">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-coral-600 leading-none">{packedItems}</div>
                  <div className="text-xs text-stone-600 uppercase tracking-wide leading-none">Packed</div>
                </div>
  
              </div>
              
              {/* Mini Progress Bar */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="flex-1 h-1 bg-stone-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-slate-900 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs text-stone-600 font-semibold whitespace-nowrap">{progress}%</span>
              </div>
              



                          {/* Compact Search */}
                          <div className="relative min-w-0 flex-1 max-w-48">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 bg-stone-50 border border-stone-200 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-coral-500 focus:border-coral-500 min-h-[44px]"
                />
              </div>
              
              {/* Filter Dropdown */}
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-coral-500 min-h-[44px]"
              >
                <option value="luggage_type">Luggage</option>
                <option value="category">Category</option>
                {list?.list_type === 'group' && (
                  <option value="assignee">Person</option>
                )}
              </select>

              {/* Action Buttons - Desktop Inline */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddItem(!showAddItem)}
                  className={`flex items-center justify-center w-11 h-11 rounded-full transition-colors ${
                    showAddItem 
                      ? 'bg-stone-600 text-white' 
                      : 'bg-coral-500 text-white hover:bg-coral-600'
                  }`}
                  aria-label={showAddItem ? "Close add item form" : "Add new item"}
                >
                  <Plus className={`w-5 h-5 transition-transform ${showAddItem ? 'rotate-45' : ''}`} />
                </button>

                <button
                  onClick={() => setShowAIAssist(!showAIAssist)}
                  className={`flex items-center justify-center w-11 h-11 rounded-full transition-colors ${
                    showAIAssist 
                      ? 'bg-violet-700 text-white' 
                      : 'bg-violet-600 text-white hover:bg-violet-700'
                  }`}
                  aria-label={showAIAssist ? "Hide AI assistant" : "Show AI assistant"}
                >
                  <Sparkles className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowCheckedItems(!showCheckedItems)}
                  className={`flex items-center justify-center w-11 h-11 rounded-full transition-colors ${
                    showCheckedItems 
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                      : 'bg-stone-400 text-white hover:bg-stone-500'
                  }`}
                  aria-label={showCheckedItems ? "Hide packed items" : "Show packed items"}
                >
                  {showCheckedItems ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        )}



      </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">

        {/* Mobile Search and Sort Controls */}
        <div className="md:hidden mb-6">
          <div className="flex gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500 min-h-[44px]"
              />
            </div>
            
            {/* Sort Dropdown */}
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500 min-h-[44px]"
            >
              <option value="luggage_type">Luggage</option>
              <option value="category">Category</option>
              {list?.list_type === 'group' && (
                <option value="assignee">Person</option>
              )}
            </select>
          </div>
        </div>

        {/* Add Item Form */}
        {showAddItem && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
            <h3 className="text-lg font-medium text-slate-900 mb-4">Add New Item</h3>
            <form onSubmit={handleAddItem} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Item name"
                value={newItem.name}
                onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                required
                autoFocus
              />
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 1})}
                  className="w-20 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                />
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                  className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                >
                  {['clothing', 'toiletries', 'electronics', 'documents', 'medications', 'entertainment', 'accessories', 'shoes', 'outdoor_gear', 'baby_items', 'pet_supplies', 'other'].map(key => (
                    <option key={key} value={key}>
                      {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              {/* Assignment dropdown - only shown for group lists */}
              {list?.list_type === 'group' && (
                <select
                  value={newItem.assigned_to || ''}
                  onChange={(e) => setNewItem({...newItem, assigned_to: e.target.value || null})}
                  className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                >
                  <option value="">Assign to...</option>
                  {[...(family?.parents || []), ...(family?.kids || [])].map(member => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              )}
              <select
                value={newItem.luggage_type}
                onChange={(e) => setNewItem({...newItem, luggage_type: e.target.value})}
                className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              >
                <option value="Checked Luggage">Checked Luggage</option>
                <option value="Carry-On">Carry-On</option>
                <option value="Personal Item">Personal Item</option>
                <option value="To Wear">To Wear</option>
              </select>
              <input
                type="text"
                placeholder="Notes (optional)"
                value={newItem.notes}
                onChange={(e) => setNewItem({...newItem, notes: e.target.value})}
                className="md:col-span-2 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              />
              <div className="md:col-span-2 flex items-center justify-between">
                <label className="flex items-center gap-2 text-slate-900">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-coral-500 border-stone-300 rounded focus:ring-coral-500"
                  />
                  Essential item
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddItem(false)}
                    className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors"
                  >
                    Add Item
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* AI Assist */}
        {showAIAssist && (
          <div className="bg-violet-50 rounded-xl border border-violet-200 p-6 mb-6 pb-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Bot className="w-5 h-5 text-violet-600" />
              AI Packing Assistant
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-4 bg-white rounded-lg border border-violet-200">
                <Lightbulb className="w-5 h-5 text-violet-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-normal text-slate-900">Smart Suggestions</p>
                  <p className="text-sm text-stone-600">
                    Based on your {list.trip_type} trip to {list.destination || 'your destination'} in {list.weather_expected} weather, 
                    consider adding: sunscreen, comfortable walking shoes, and a light jacket.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button 
                  onClick={() => showAISuggestions('essentials')}
                  className="p-3 bg-white rounded-lg border border-violet-200 hover:border-violet-400 hover:bg-violet-50 text-sm text-left transition-colors"
                >
                  <div className="font-normal text-slate-900">Auto-suggest essentials</div>
                  <div className="text-xs text-stone-600 mt-1">Add common items based on trip type</div>
                </button>
                <button 
                  onClick={() => showAISuggestions('weather')}
                  className="p-3 bg-white rounded-lg border border-violet-200 hover:border-violet-400 hover:bg-violet-50 text-sm text-left transition-colors"
                >
                  <div className="font-normal text-slate-900">Weather-based items</div>
                  <div className="text-xs text-stone-600 mt-1">Suggest items for {list.weather_expected} conditions</div>
                </button>
                <button 
                  onClick={() => showAISuggestions('activities')}
                  className="p-3 bg-white rounded-lg border border-violet-200 hover:border-violet-400 hover:bg-violet-50 text-sm text-left transition-colors"
                >
                  <div className="font-normal text-slate-900">Activity suggestions</div>
                  <div className="text-xs text-stone-600 mt-1">Add items for popular activities</div>
                </button>
                <button 
                  onClick={() => showAISuggestions('forgotten')}
                  className="p-3 bg-white rounded-lg border border-violet-200 hover:border-violet-400 hover:bg-violet-50 text-sm text-left transition-colors"
                >
                  <div className="font-normal text-slate-900">Check for missing</div>
                  <div className="text-xs text-stone-600 mt-1">Find commonly forgotten items</div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Items List */}
        {Object.keys(groupedItems).length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
            <Package className="w-16 h-16 text-stone-400 mx-auto mb-4" />
            <h3 className="text-xl font-normal text-slate-900 mb-2">
              {searchQuery || categoryFilter !== 'all' || assigneeFilter !== 'all' || !showCheckedItems
                ? 'No items match your filters' 
                : 'No items added yet'}
            </h3>
            <p className="text-stone-600 mb-6">
              {searchQuery || categoryFilter !== 'all' || assigneeFilter !== 'all' || !showCheckedItems
                ? 'Try adjusting your filters'
                : 'Start adding items to your packing list'}
            </p>
            {!showAddItem && (
              <button
                onClick={() => setShowAddItem(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-coral-500 text-white font-normal rounded-lg hover:bg-coral-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Your First Item
              </button>
            )}
          </div>
        ) : (
          <>

            <div className="space-y-6">
              {groupBy === 'luggage_type' ? (
              // Hierarchical rendering for luggage type
              (() => {
                // Find the luggage type with the most items (this gets subcategories)
                const luggageTypeCounts = Object.entries(groupedItems).map(([type, categoryGroups]) => ({
                  type,
                  count: Object.values(categoryGroups).flat().length
                }));
                const largestLuggageType = luggageTypeCounts.reduce((max, current) => 
                  current.count > max.count ? current : max, { type: null, count: 0 }
                ).type;

                return Object.entries(groupedItems).map(([luggageType, categoryGroups]) => {
                  const luggageLabel = luggageTypeLabels[luggageType] || luggageType;
                  const LuggageIcon = luggageType === 'To Wear' ? Shirt :
                                     luggageType === 'Carry-On' ? Briefcase : 
                                     luggageType === 'Checked Luggage' ? Luggage :
                                     luggageType === 'Personal Item' ? Backpack : ShoppingBag;
                  
                  // Calculate total items across all categories in this luggage type
                  const totalItems = Object.values(categoryGroups).flat().length;
                  const showSubcategories = luggageType === largestLuggageType;

                  return (
                    <div key={luggageType} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                      {/* Luggage Type Header */}
                      <div className="px-4 py-2 bg-gradient-to-r from-slate-900 to-slate-800 border-b border-stone-200">
                        <h2 className="font-medium text-sm text-stone-50 flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            {luggageLabel}
                            <span className="text-sm font-normal text-stone-50 px-3 py-1 rounded-full">
                              {totalItems} items
                            </span>
                          </div>
                        </h2>
                      </div>

                      {showSubcategories ? (
                        /* Show subcategories for the largest luggage type */
                        <div className="divide-y divide-stone-100">
                          {Object.entries(categoryGroups).map(([category, items]) => {
                            const categoryConfig = getCategoryIcon(category);
                            const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ');

                            return (
                              <div key={category}>
                                {/* Category Subheader */}
                                <div className={`px-4 py-3 ${categoryConfig.bg} border-b border-stone-100`}>
                                  <h4 className={`font-normal text-xs ${categoryConfig.color} flex items-center gap-2`}>
                                    <div className="p-1 rounded-md">
                                      {categoryConfig.icon}
                                    </div>
                                    {categoryLabel}
                                    <span className={`text-xs font-normal ${categoryConfig.color} bg-white px-2 py-1 rounded-full`}>
                                      {items.length}
                                    </span>
                                  </h4>
                                </div>
                                
                                {/* Items in this category */}
                                <div className="divide-y divide-stone-50">
                                  {items.map(item => (
                                    <PackingItem
                                      key={item.id}
                                      item={item}
                                      onTogglePacked={handleTogglePacked}
                                      onUpdate={handleUpdateItem}
                                      onDelete={handleDeleteItem}
                                      onCopy={handleCopyItem}
                                      familyMembers={[...(family?.parents || []), ...(family?.kids || [])]}
                                      isInTransition={itemsInTransition.has(item.id)}
                                      groupBy={groupBy}
                                      listType={list?.list_type || 'individual'}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Show items directly without subcategories for smaller luggage types */
                        <div className="divide-y divide-stone-50">
                          {Object.values(categoryGroups).flat().map(item => (
                            <PackingItem
                              key={item.id}
                              item={item}
                              onTogglePacked={handleTogglePacked}
                              onUpdate={handleUpdateItem}
                              onDelete={handleDeleteItem}
                              onCopy={handleCopyItem}
                              familyMembers={[...(family?.parents || []), ...(family?.kids || [])]}
                              isInTransition={itemsInTransition.has(item.id)}
                              groupBy={groupBy}
                              listType={list?.list_type || 'individual'}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()
            ) : (
              // Standard flat rendering for other grouping types
              Object.entries(groupedItems).map(([group, items]) => {
                // Get group label based on groupBy type
                let groupLabel = group;
                let groupIcon = null;
                let groupBg = 'bg-stone-50';
                
                if (groupBy === 'category') {
                  const categoryConfig = getCategoryIcon(group);
                  groupIcon = categoryConfig.icon;
                  groupLabel = group.charAt(0).toUpperCase() + group.slice(1).replace('_', ' ');
                  groupBg = categoryConfig.bg;
                } else if (groupBy === 'assignee') {
                  const member = [...(family?.parents || []), ...(family?.kids || [])].find(m => m.id === group);
                  if (member) {
                    groupLabel = member.name;
                  } else if (group === 'unassigned') {
                    groupLabel = 'Unassigned';
                    groupIcon = <User className="w-4 h-4" />;
                  }
                }

                const isCollapsed = collapsedCategories.has(group);
                const packedCount = items.filter(item => item.packed).length;
                const categoryProgress = items.length > 0 ? Math.round((packedCount / items.length) * 100) : 0;

                return (
                  <div key={group} className="mb-6">
                    {/* Compact Category Header */}
                    <button
                      onClick={() => toggleCategoryCollapse(group)}
                      className={`w-full flex items-center gap-2 p-4 ${groupBg} rounded-lg border border-stone-200 hover:border-stone-300 transition-colors`}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        {groupIcon}
                        <span className="text-sm font-semibold text-slate-900">{groupLabel}</span>
                        <span className="text-xs text-stone-600 bg-white px-2 py-1 rounded-full font-medium">
                          {items.length}
                        </span>
                        {categoryProgress > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-8 h-1 bg-stone-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                                style={{ width: `${categoryProgress}%` }}
                              />
                            </div>
                            <span className="text-xs text-stone-600">{categoryProgress}%</span>
                          </div>
                        )}
                      </div>
                      <div className={`transform transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}>
                        <Plus className="w-4 h-4 text-stone-500" />
                      </div>
                    </button>

                    {/* Items List */}
                    {!isCollapsed && (
                      <div className="mt-2 space-y-2">
                        {items.map(item => (
                          <PackingItem
                            key={item.id}
                            item={item}
                            onTogglePacked={handleTogglePacked}
                            onUpdate={handleUpdateItem}
                            onDelete={handleDeleteItem}
                            onCopy={handleCopyItem}
                            familyMembers={[...(family?.parents || []), ...(family?.kids || [])]}
                            isInTransition={itemsInTransition.has(item.id)}
                            groupBy={groupBy}
                            listType={list?.list_type || 'individual'}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
            </div>
          </>
        )}
      </div>

      {/* AI Suggestions Modal */}
      {showSuggestionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-stone-200 shadow-lg">
            {/* Header */}
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">
                  AI Suggestions: {suggestionType.charAt(0).toUpperCase() + suggestionType.slice(1)}
                </h2>
                <button
                  onClick={() => setShowSuggestionsModal(false)}
                  className="text-stone-400 hover:text-stone-600 p-2 hover:bg-stone-100 rounded-lg transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-stone-600 mt-2">
                Review and select the items you'd like to add to your packing list. Items you already have are shown but disabled.
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-3">
                {pendingSuggestions.map((suggestion, index) => (
                  <div 
                    key={index}
                    className={`p-4 border rounded-lg transition-all ${
                      suggestion.exists 
                        ? 'bg-stone-50 border-stone-200 opacity-60' 
                        : suggestion.selected 
                          ? 'bg-violet-50 border-violet-300' 
                          : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => !suggestion.exists && toggleSuggestionSelection(index)}
                        disabled={suggestion.exists}
                        className={`flex-shrink-0 mt-0.5 ${suggestion.exists ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {suggestion.exists ? (
                          <CheckSquare className="w-5 h-5 text-stone-400" />
                        ) : (
                          suggestion.selected ? (
                            <CheckSquare className="w-5 h-5 text-violet-600" />
                          ) : (
                            <Square className="w-5 h-5 text-stone-400 hover:text-stone-600" />
                          )
                        )}
                      </button>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-medium ${suggestion.exists ? 'text-stone-500' : 'text-slate-900'}`}>
                            {suggestion.name}
                          </span>
                          {suggestion.quantity > 1 && (
                            <span className="px-2 py-1 bg-stone-200 rounded-full text-xs font-normal">
                              x{suggestion.quantity}
                            </span>
                          )}

                          <span className="px-2 py-1 bg-azure-100 text-azure-700 rounded-full text-xs font-normal">
                            {suggestion.category}
                          </span>
                          {suggestion.exists && (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-normal">
                              Already added
                            </span>
                          )}
                        </div>
                        {suggestion.notes && (
                          <p className="text-sm text-stone-600 mt-1">{suggestion.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-6 border-t border-stone-200 mt-6">
                <button
                  onClick={() => setShowSuggestionsModal(false)}
                  className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addSelectedSuggestions}
                  disabled={!pendingSuggestions.some(s => s.selected && !s.exists)}
                  className="flex-1 bg-coral-500 text-white py-2 px-4 rounded-lg hover:bg-coral-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add {pendingSuggestions.filter(s => s.selected && !s.exists).length} Selected Items
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy to Other Lists Modal */}
      {showCopyModal && itemToCopy && (
        <CopyToListsModal
          item={itemToCopy}
          allLists={lists}
          currentListId={list.id}
          onCopy={handleCopyToLists}
          onClose={() => {
            setShowCopyModal(false);
            setItemToCopy(null);
          }}
        />
      )}

      {/* Weather Modal - Mobile Only */}
      {showWeatherModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 md:hidden">
          <div className="bg-white rounded-xl max-w-sm w-full border border-stone-200 shadow-lg">
            {/* Header */}
            <div className="p-4 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Weather Details</h2>
                <button
                  onClick={() => setShowWeatherModal(false)}
                  className="text-stone-400 hover:text-stone-600 p-2 hover:bg-stone-100 rounded transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close weather details"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              {list.trip_legs && list.trip_legs.length > 0 ? (
                <div className="space-y-4">
                  {list.trip_legs.map((leg, index) => (
                    <div key={index} className="border-b border-stone-100 pb-3 last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-stone-500" />
                        <span className="font-medium text-slate-900">{leg.location}</span>
                        {leg.weather_data && (
                          <span className="text-lg">
                            {getWeatherIcon(leg.weather_data.weatherCategory || 'mild')}
                          </span>
                        )}
                      </div>
                      
                      {leg.weather_data && leg.weather_data.forecast && leg.weather_data.forecast.length > 0 && (
                        <div className="text-sm text-stone-600 mb-2">
                          <span className="font-medium">
                            {Math.min(...leg.weather_data.forecast.map(d => d.tempMin))}°-{Math.max(...leg.weather_data.forecast.map(d => d.tempMax))}°F
                          </span>
                        </div>
                      )}
                      
                      {leg.weather_data && getWeatherWarnings(leg.weather_data).length > 0 && (
                        <div className="space-y-1">
                          {getWeatherWarnings(leg.weather_data).map((warning, idx) => (
                            <div key={idx} className="text-sm text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                              {warning}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : list.destination && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-stone-500" />
                    <span className="font-medium text-slate-900">{list.destination}</span>
                    {(list.weather_data || list.weather_expected) && (
                      <span className="text-lg">
                        {list.weather_data ? 
                          getWeatherIcon(list.weather_data.weatherCategory || 'mild') :
                          getWeatherIcon(list.weather_expected || 'mild')
                        }
                      </span>
                    )}
                  </div>
                  
                  {list.weather_data?.forecast && list.weather_data.forecast.length > 0 ? (
                    <div className="text-sm text-stone-600 mb-2">
                      <span className="font-medium">
                        {Math.min(...list.weather_data.forecast.map(d => d.tempMin))}°-{Math.max(...list.weather_data.forecast.map(d => d.tempMax))}°F
                      </span>
                    </div>
                  ) : list.weather_expected && (
                    <div className="text-sm text-stone-600 mb-2">
                      <span className="font-medium capitalize">{list.weather_expected} conditions</span>
                    </div>
                  )}
                  
                  {list.weather_data && getWeatherWarnings(list.weather_data).length > 0 && (
                    <div className="space-y-1">
                      {getWeatherWarnings(list.weather_data).map((warning, idx) => (
                        <div key={idx} className="text-sm text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                          {warning}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Individual Item Component
function PackingItem({ item, onTogglePacked, onUpdate, onDelete, onCopy, familyMembers, isInTransition, groupBy, listType }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: item.name,
    quantity: item.quantity,
    notes: item.notes || '',
    assigned_to: item.assigned_to,
    luggage_type: item.luggage_type || 'Checked Luggage',
    category: item.category || 'other'
  });

  const handleSave = () => {
    onUpdate(item.id, editData);
    setIsEditing(false);
  };

  // Prefix unused variables with underscore
  const _assignedMember = familyMembers.find(m => m.id === item.assigned_to);

  const _getCategoryIcon = (category) => {
    const iconConfig = {
      clothing: { icon: Shirt, color: 'text-azure-600', bg: 'bg-azure-50' },
      toiletries: { icon: Droplets, color: 'text-cyan-600', bg: 'bg-cyan-50' },
      electronics: { icon: Smartphone, color: 'text-violet-600', bg: 'bg-violet-50' },
      documents: { icon: FileText, color: 'text-orange-600', bg: 'bg-orange-50' },
      medications: { icon: Pill, color: 'text-red-600', bg: 'bg-red-50' },
      entertainment: { icon: Gamepad2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
      accessories: { icon: ShoppingBag, color: 'text-pink-600', bg: 'bg-pink-50' },
      shoes: { icon: Package, color: 'text-amber-600', bg: 'bg-amber-50' },
      outdoor_gear: { icon: Backpack, color: 'text-emerald-600', bg: 'bg-emerald-50' },
      baby_items: { icon: Baby, color: 'text-rose-600', bg: 'bg-rose-50' },
      pet_supplies: { icon: PawPrint, color: 'text-indigo-600', bg: 'bg-indigo-50' },
      other: { icon: Package, color: 'text-stone-600', bg: 'bg-stone-50' }
    };
    const config = iconConfig[category] || iconConfig.other;
    const IconComponent = config.icon;
    return {
      icon: <IconComponent className={`w-3 h-3 ${config.color}`} />,
      color: config.color,
      bg: config.bg
    };
  };

  if (isEditing) {
    return (
      <div className="p-4 md:p-6 bg-stone-50 rounded-lg border border-stone-200">
        {/* Mobile: Structured Layout */}
        <div className="md:hidden space-y-3">
          {/* Row 1: Name + Quantity */}
          <div className="flex gap-2">
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData({...editData, name: e.target.value})}
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
            <input
              type="number"
              min="1"
              value={editData.quantity}
              onChange={(e) => setEditData({...editData, quantity: parseInt(e.target.value) || 1})}
              className="w-16 px-2 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500 text-center"
            />
          </div>
          
          {/* Row 2: Category + Luggage Type */}
          <div className="flex gap-2">
            <select
              value={editData.category}
              onChange={(e) => setEditData({...editData, category: e.target.value})}
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            >
              {['clothing', 'toiletries', 'electronics', 'documents', 'medications', 'entertainment', 'accessories', 'shoes', 'outdoor_gear', 'baby_items', 'pet_supplies', 'other'].map(key => (
                <option key={key} value={key}>
                  {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
            <select
              value={editData.luggage_type}
              onChange={(e) => setEditData({...editData, luggage_type: e.target.value})}
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            >
              <option value="Checked Luggage">Checked</option>
              <option value="Carry-On">Carry-On</option>
              <option value="Personal Item">Personal</option>
              <option value="To Wear">To Wear</option>
            </select>
          </div>
          
          {/* Row 3: Assignment (if group) + Action Buttons */}
          <div className="flex gap-2 items-center">
            {listType === 'group' && (
              <select
                value={editData.assigned_to || ''}
                onChange={(e) => setEditData({...editData, assigned_to: e.target.value || null})}
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              >
                <option value="">Unassigned</option>
                {familyMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={handleSave}
                className="p-2.5 bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Save changes"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="p-2.5 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Cancel editing"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Notes field */}
          <input
            type="text"
            placeholder="Notes..."
            value={editData.notes}
            onChange={(e) => setEditData({...editData, notes: e.target.value})}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
          />
        </div>

        {/* Desktop: Original Flex Layout */}
        <div className="hidden md:flex flex-wrap gap-3 items-center">
          <input
            type="text"
            value={editData.name}
            onChange={(e) => setEditData({...editData, name: e.target.value})}
            className="flex-1 min-w-[200px] px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
          />
          <input
            type="number"
            min="1"
            value={editData.quantity}
            onChange={(e) => setEditData({...editData, quantity: parseInt(e.target.value) || 1})}
            className="w-20 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
          />
          <select
            value={editData.category}
            onChange={(e) => setEditData({...editData, category: e.target.value})}
            className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
          >
            {['clothing', 'toiletries', 'electronics', 'documents', 'medications', 'entertainment', 'accessories', 'shoes', 'outdoor_gear', 'baby_items', 'pet_supplies', 'other'].map(key => (
              <option key={key} value={key}>
                {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
              </option>
            ))}
          </select>
          <select
            value={editData.luggage_type}
            onChange={(e) => setEditData({...editData, luggage_type: e.target.value})}
            className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
          >
            <option value="Checked Luggage">Checked Luggage</option>
            <option value="Carry-On">Carry-On</option>
            <option value="Personal Item">Personal Item</option>
            <option value="To Wear">To Wear</option>
          </select>
          {/* Assignment dropdown - only shown for group lists */}
          {listType === 'group' && (
            <select
              value={editData.assigned_to || ''}
              onChange={(e) => setEditData({...editData, assigned_to: e.target.value || null})}
              className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            >
              <option value="">Unassigned</option>
              {familyMembers.map(member => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={handleSave}
            className="p-3 bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Save changes"
          >
            <Check className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsEditing(false)}
            className="p-3 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Cancel editing"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Desktop: Notes field */}
        <input
          type="text"
          placeholder="Notes..."
          value={editData.notes}
          onChange={(e) => setEditData({...editData, notes: e.target.value})}
          className="hidden md:block mt-3 w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
        />
      </div>
    );
  }

  // Determine the visual state - show as packed if item is packed OR if it's transitioning from unpacked to packed
  const showAsPacked = item.packed || (isInTransition && !item.packed);
  const _showAsUnpacked = !item.packed || (isInTransition && item.packed);

  return (
    <div className={`bg-white rounded-lg border border-stone-200 p-3 md:p-4 hover:border-stone-300 transition-all duration-300 ${isInTransition ? 'transform scale-[0.98] opacity-90' : ''}`}>
      <div className="flex items-center gap-2 md:gap-3">
        <button
          onClick={() => onTogglePacked(item)}
          className="transition-colors flex-shrink-0 p-2 hover:bg-stone-50 rounded min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label={item.packed ? "Mark as unpacked" : "Mark as packed"}
          disabled={isInTransition}
        >
          {showAsPacked ? (
            <CheckSquare className={`w-5 h-5 text-coral-600 ${isInTransition ? 'animate-pulse' : ''}`} />
          ) : (
            <Square className="w-5 h-5 text-stone-400 hover:text-coral-500" />
          )}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium transition-all duration-300 ${
              showAsPacked 
                ? 'line-through text-stone-500' 
                : 'text-slate-900'
            } ${isInTransition ? 'italic' : ''}`}>
              {item.name}
            </span>
            {item.quantity > 1 && (
              <span className="px-2 py-0.5 bg-stone-100 text-stone-700 rounded-full text-xs font-medium">
                x{item.quantity}
              </span>
            )}
            {isInTransition && (
              <span className="px-2 py-0.5 bg-coral-100 text-coral-700 rounded-full text-xs font-medium animate-pulse">
                {item.packed ? 'Unpacking...' : 'Packing...'}
              </span>
            )}
          </div>
          {item.notes && (
            <p className={`text-xs text-stone-600 mt-1 transition-all duration-300 ${showAsPacked ? 'opacity-60' : ''}`}>{item.notes}</p>
          )}
        </div>

        {/* Actions - Same row for both mobile and desktop */}
        <div className="flex items-center gap-0.5 md:gap-1 flex-shrink-0">
          {/* Only show luggage tag if NOT in luggage view */}
          {groupBy !== 'luggage_type' && item.luggage_type && item.luggage_type !== 'Checked Luggage' && (
            <span className="px-2 py-1 bg-azure-100 text-azure-700 rounded-full text-xs font-medium mr-1 md:mr-0">
              {item.luggage_type}
            </span>
          )}
          
          <button
            onClick={() => setIsEditing(true)}
            className="p-1.5 md:p-2 hover:bg-stone-100 rounded transition-colors min-w-[40px] min-h-[40px] md:min-w-[44px] md:min-h-[44px] flex items-center justify-center"
            aria-label="Edit item"
          >
            <Edit className="w-3.5 h-3.5 md:w-4 md:h-4 text-stone-500" />
          </button>
          <button
            onClick={() => onCopy(item)}
            className="p-1.5 md:p-2 hover:bg-azure-50 rounded transition-colors min-w-[40px] min-h-[40px] md:min-w-[44px] md:min-h-[44px] flex items-center justify-center"
            title="Copy to other lists"
            aria-label="Copy item to other lists"  
          >
            <Copy className="w-3.5 h-3.5 md:w-4 md:h-4 text-stone-500 hover:text-azure-600" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 md:p-2 hover:bg-red-50 rounded transition-colors min-w-[40px] min-h-[40px] md:min-w-[44px] md:min-h-[44px] flex items-center justify-center"
            aria-label="Delete item"
          >
            <Trash2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-stone-500 hover:text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Copy to Lists Modal Component
function CopyToListsModal({ item, allLists, currentListId, onCopy, onClose }) {
  const [selectedListIds, setSelectedListIds] = useState([]);

  // Filter out the current list
  const otherLists = allLists.filter(list => list.id !== currentListId);

  const toggleListSelection = (listId) => {
    setSelectedListIds(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const handleCopy = () => {
    onCopy(selectedListIds);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-stone-200 shadow-lg">
        {/* Header */}
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Copy "{item.name}" to Other Lists
            </h2>
            <button
              onClick={onClose}
              className="text-stone-400 hover:text-stone-600 p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {otherLists.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-stone-500 mb-2">No other packing lists available</div>
              <div className="text-sm text-stone-400">Create more packing lists to copy items between them.</div>
            </div>
          ) : (
            <>
              <div className="text-sm text-stone-600 mb-4">
                Select the lists you want to copy this item to:
              </div>
              
              <div className="space-y-2 mb-6">
                {otherLists.map(list => (
                  <label 
                    key={list.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedListIds.includes(list.id)}
                      onChange={() => toggleListSelection(list.id)}
                      className="h-4 w-4 text-coral-600 focus:ring-coral-500 border-stone-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{list.name}</div>
                      {list.destination && (
                        <div className="text-sm text-stone-500">{list.destination}</div>
                      )}
                    </div>
                    <div className="text-xs text-stone-400">
                      {list.items?.length || 0} items
                    </div>
                  </label>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCopy}
                  disabled={selectedListIds.length === 0}
                  className="flex-1 bg-coral-500 text-white py-2 px-4 rounded-lg hover:bg-coral-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
                >
                  Copy to {selectedListIds.length} List{selectedListIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}